import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { dokumenteApi, vertraegeApi } from '../api';

// ─── Ampel für Risikostufen ────────────────────────────────────────────────────
const RISIKO_FARBE = {
  keine: '#34d399', hinweis: '#38bdf8', mittel: '#fbbf24',
  hoch: '#f87171', kritisch: '#ef4444', unklar: '#94a3b8',
};

function konfidenzBadge(k) {
  if (k == null) return null;
  const pct = Math.round(k * 100);
  const color = pct >= 80 ? '#34d399' : pct >= 50 ? '#fbbf24' : '#f87171';
  return <span style={{ ...badge, color, borderColor: `${color}66`, background: `${color}14` }}>{pct}%</span>;
}

export default function DokumentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [dok, setDok] = useState(null);
  const [analyse, setAnalyse] = useState(null);
  const [felder, setFelder] = useState([]);
  const [vorschlaege, setVorschlaege] = useState([]);
  const [vertraege, setVertraege] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState(null);
  const [activeTab, setActiveTab] = useState('felder');
  const [meldung, setMeldung] = useState('');
  const [neuerVertragTitel, setNeuerVertragTitel] = useState('');
  const [zielVertragId, setZielVertragId] = useState('');

  const ladeAlles = useCallback(async () => {
    try {
      const [analyseRes, felderRes, vorschlaege2Res, vertraegeRes] = await Promise.all([
        dokumenteApi.analyseDetail(id),
        dokumenteApi.felder(id),
        dokumenteApi.vorschlaege(id),
        vertraegeApi.list(),
      ]);
      setDok(analyseRes.data.dokument);
      setAnalyse(analyseRes.data.analyse);
      setFelder(analyseRes.data.felder || []);
      setVorschlaege(analyseRes.data.vorschlaege || []);
      setVertraege(vertraegeRes.data || []);
    } catch (e) {
      setError('Dokument konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { ladeAlles(); }, [ladeAlles]);

  // Polling für Verarbeitungsstatus
  useEffect(() => {
    if (!dok) return;
    if (dok.verarbeitungsstatus === 'abgeschlossen' || dok.verarbeitungsstatus === 'fehlgeschlagen') return;
    const interval = setInterval(async () => {
      try {
        const res = await dokumenteApi.status(id);
        setStatus(res.data);
        if (res.data.verarbeitungsstatus === 'abgeschlossen' || res.data.verarbeitungsstatus === 'fehlgeschlagen') {
          clearInterval(interval);
          ladeAlles();
        }
      } catch {}
    }, 3000);
    return () => clearInterval(interval);
  }, [dok, id, ladeAlles]);

  const onFeldAktion = async (feldId, aktion) => {
    try {
      await dokumenteApi.feldBestaetigen(id, feldId, aktion);
      const res = await dokumenteApi.felder(id);
      setFelder(res.data.felder || []);
      setMeldung(`Feld ${aktion === 'bestaetigen' ? 'bestätigt' : 'abgelehnt'}.`);
      setTimeout(() => setMeldung(''), 3000);
    } catch { setMeldung('Fehler beim Speichern.'); }
  };

  const onFelderUebernehmen = async () => {
    if (!zielVertragId) return setMeldung('Bitte Zielvertrag auswählen.');
    try {
      const bestaetigte = felder.filter(f => f.bestaetigt && !f.abgelehnt).map(f => f.id);
      const res = await dokumenteApi.felderUebernehmen(id, { vertrag_id: Number(zielVertragId), felder_ids: bestaetigte });
      setMeldung(`${res.data.uebernommen} Felder übernommen.`);
      setTimeout(() => setMeldung(''), 4000);
    } catch (e) { setMeldung(e.response?.data?.error || 'Fehler bei Übernahme.'); }
  };

  const onNeuerVertrag = async () => {
    if (!neuerVertragTitel.trim()) return setMeldung('Bitte Vertragsbezeichnung eingeben.');
    try {
      const res = await dokumenteApi.neuerVertrag(id, { titel: neuerVertragTitel });
      setMeldung('Vertrag erstellt!');
      setTimeout(() => navigate(`/vertraege/${res.data.vertrag_id}`), 1500);
    } catch (e) { setMeldung(e.response?.data?.error || 'Fehler beim Erstellen.'); }
  };

  const onZuordnen = async (vertragId) => {
    try {
      await dokumenteApi.zuordnen(id, vertragId);
      setMeldung('Dokument zugeordnet.');
      ladeAlles();
    } catch (e) { setMeldung(e.response?.data?.error || 'Fehler.'); }
  };

  const onAnalyseNeustart = async () => {
    try {
      await dokumenteApi.analysieren(id);
      setMeldung('Analyse gestartet...');
      setDok(d => ({ ...d, verarbeitungsstatus: 'laeuft' }));
    } catch { setMeldung('Fehler beim Neustart.'); }
  };

  if (loading) return <Screen text="Dokument wird geladen..." />;
  if (error || !dok) return <Screen title="Fehler" text={error || 'Dokument nicht gefunden'} />;

  const liveStatus = status || dok;
  const previewUrl = dokumenteApi.previewUrl(`/uploads/${dok.dateiname}`);
  const verarbNoch = liveStatus.verarbeitungsstatus !== 'abgeschlossen' && liveStatus.verarbeitungsstatus !== 'fehlgeschlagen';
  const risikoFarbe = dok.risiko_stufe ? (RISIKO_FARBE[dok.risiko_stufe] || '#94a3b8') : null;

  return (
    <div style={page}>
      {/* Header */}
      <div style={header}>
        <div>
          <div style={eyebrow}>Dokumentenanalyse</div>
          <h1 style={title}>{dok.originaldateiname || dok.dateiname}</h1>
          <div style={subtitle}>
            {dok.klassifikation && <span style={typChip}>{dok.klassifikation}</span>}
            {dok.risiko_stufe && dok.risiko_stufe !== 'keine' && (
              <span style={{ ...typChip, color: risikoFarbe, borderColor: `${risikoFarbe}66`, background: `${risikoFarbe}14` }}>
                Risiko: {dok.risiko_stufe}
              </span>
            )}
            {dok.dublett_typ && (
              <span style={{ ...typChip, color: '#f87171', borderColor: '#f8717166', background: '#f8717114' }}>
                {dok.dublett_typ === 'identisch' ? 'Duplikat' : 'Ähnlich'}
              </span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button className="btn-secondary" onClick={() => navigate('/dokumente')}>Zurück</button>
          <button className="btn-secondary" onClick={() => navigate(`/vergleich?type=dokumente&leftDoc=${id}`)}>⚖️ Vergleichen</button>
          {dok.vertrag_id && dok.vertrag_id > 0 && (
            <button className="btn-secondary" onClick={() => navigate(`/vertraege/${dok.vertrag_id}`)}>
              Zum Vertrag
            </button>
          )}
          <button className="btn-primary" onClick={onAnalyseNeustart}>Analyse neu starten</button>
        </div>
      </div>

      {meldung && <div style={notice}>{meldung}</div>}

      {/* Verarbeitungsstatus */}
      {verarbNoch && (
        <div style={statusBox}>
          <span style={spinner}>⏳</span>
          <span>Verarbeitung läuft: <strong>{liveStatus.verarbeitungsschritt || liveStatus.verarbeitungsstatus}</strong></span>
          <span style={progressBar}>
            <span style={{ ...progressFill, width: `${liveStatus.verarbeitungsfortschritt || 0}%` }} />
          </span>
          <span style={{ color: '#94a3b8', fontSize: 12 }}>{liveStatus.verarbeitungsfortschritt || 0}%</span>
        </div>
      )}
      {liveStatus.verarbeitungsstatus === 'fehlgeschlagen' && (
        <div style={{ ...notice, borderColor: '#f87171', background: '#2b0a0a', color: '#f87171' }}>
          Verarbeitung fehlgeschlagen: {liveStatus.fehlermeldung}
        </div>
      )}

      <div style={twoCol}>
        {/* Linke Spalte: Vorschau */}
        <section style={panel}>
          <h2 style={sectionTitle}>Vorschau</h2>
          <Preview filePath={previewUrl} mimeType={dok.dateityp} name={dok.originaldateiname} />
          <div style={{ marginTop: 12, display: 'grid', gap: 7 }}>
            <Info label="Dateityp" value={dok.dateityp} />
            <Info label="Größe" value={dok.groesse ? `${Math.round(dok.groesse / 1024)} KB` : '–'} />
            <Info label="Hochgeladen" value={formatDate(dok.hochgeladen_am)} />
            <Info label="Vertrag" value={dok.vertrag_titel || (dok.vertrag_id > 0 ? `ID ${dok.vertrag_id}` : 'Nicht zugeordnet')} />
            <Info label="SHA256" value={dok.sha256 ? dok.sha256.slice(0, 16) + '…' : '–'} />
          </div>
        </section>

        {/* Rechte Spalte: Tabs */}
        <div style={{ display: 'grid', gap: 14, alignContent: 'start' }}>
          <div style={tabRow}>
            {[['felder', 'Erkannte Felder'], ['analyse', 'Analyse'], ['zuordnung', 'Zuordnung']].map(([key, label]) => (
              <button key={key} style={activeTab === key ? activeTabBtn : tabBtn} onClick={() => setActiveTab(key)}>
                {label}
              </button>
            ))}
          </div>

          {activeTab === 'felder' && (
            <FelderTab felder={felder} onAktion={onFeldAktion} />
          )}
          {activeTab === 'analyse' && (
            <AnalyseTab analyse={analyse} risiken={analyse?.risiken || []} />
          )}
          {activeTab === 'zuordnung' && (
            <ZuordnungTab
              dok={dok}
              vorschlaege={vorschlaege}
              vertraege={vertraege}
              felder={felder}
              zielVertragId={zielVertragId}
              setZielVertragId={setZielVertragId}
              neuerVertragTitel={neuerVertragTitel}
              setNeuerVertragTitel={setNeuerVertragTitel}
              onZuordnen={onZuordnen}
              onFelderUebernehmen={onFelderUebernehmen}
              onNeuerVertrag={onNeuerVertrag}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function FelderTab({ felder, onAktion }) {
  if (felder.length === 0) {
    return (
      <section style={panel}>
        <h2 style={sectionTitle}>Erkannte Felder</h2>
        <EmptyState text="Noch keine Felder erkannt. Bitte Analyse starten." />
      </section>
    );
  }

  const bestaetigte = felder.filter(f => f.bestaetigt && !f.abgelehnt);
  const ausstehende = felder.filter(f => !f.bestaetigt && !f.abgelehnt);
  const abgelehnte = felder.filter(f => f.abgelehnt);

  return (
    <section style={panel}>
      <h2 style={sectionTitle}>Erkannte Felder ({felder.length})</h2>
      {ausstehende.length > 0 && (
        <>
          <h3 style={subTitle}>Zur Prüfung</h3>
          {ausstehende.map(f => <FeldCard key={f.id} feld={f} onAktion={onAktion} />)}
        </>
      )}
      {bestaetigte.length > 0 && (
        <>
          <h3 style={{ ...subTitle, color: '#34d399' }}>Bestätigt ({bestaetigte.length})</h3>
          {bestaetigte.map(f => <FeldCard key={f.id} feld={f} onAktion={onAktion} />)}
        </>
      )}
      {abgelehnte.length > 0 && (
        <>
          <h3 style={{ ...subTitle, color: '#94a3b8' }}>Abgelehnt ({abgelehnte.length})</h3>
          {abgelehnte.map(f => <FeldCard key={f.id} feld={f} onAktion={onAktion} />)}
        </>
      )}
    </section>
  );
}

function FeldCard({ feld, onAktion }) {
  const istBestaetigt = feld.bestaetigt && !feld.abgelehnt;
  const istAbgelehnt = feld.abgelehnt;
  const borderColor = istBestaetigt ? '#34d399' : istAbgelehnt ? '#334155' : '#2563eb';

  return (
    <div style={{ ...feldCard, borderColor }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
        <div>
          <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 800, textTransform: 'uppercase', marginBottom: 3 }}>
            {feld.feldname}
          </div>
          <div style={{ color: '#f8fafc', fontWeight: 700, fontSize: 14 }}>
            {feld.normalisierter_wert || feld.erkannter_wert || '–'}
          </div>
          {feld.erkannter_wert !== feld.normalisierter_wert && feld.erkannter_wert && (
            <div style={{ color: '#64748b', fontSize: 11 }}>Erkannt: {feld.erkannter_wert}</div>
          )}
          {feld.fundstelle && (
            <div style={{ color: '#475569', fontSize: 11, fontStyle: 'italic', marginTop: 3 }}>
              {feld.fundstelle}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
          {konfidenzBadge(feld.konfidenz)}
          {!istBestaetigt && !istAbgelehnt && (
            <>
              <button style={confirmBtn} onClick={() => onAktion(feld.id, 'bestaetigen')}>✓</button>
              <button style={rejectBtn} onClick={() => onAktion(feld.id, 'ablehnen')}>✕</button>
            </>
          )}
          {istBestaetigt && (
            <button style={rejectBtn} title="Ablehnen" onClick={() => onAktion(feld.id, 'ablehnen')}>✕</button>
          )}
          {istAbgelehnt && (
            <button style={confirmBtn} title="Bestätigen" onClick={() => onAktion(feld.id, 'bestaetigen')}>✓</button>
          )}
        </div>
      </div>
    </div>
  );
}

function AnalyseTab({ analyse, risiken }) {
  if (!analyse) {
    return (
      <section style={panel}>
        <h2 style={sectionTitle}>Analyse</h2>
        <EmptyState text="Noch keine Analyse vorhanden." />
      </section>
    );
  }

  const zusammenfassung = analyse.zusammenfassung;
  const kurzfassung = zusammenfassung?.kurzfassung || [];
  const handlungsempfehlung = zusammenfassung?.handlungsempfehlung || [];

  return (
    <section style={panel}>
      <h2 style={sectionTitle}>Analyse</h2>
      <div style={{ display: 'grid', gap: 12 }}>
        <div style={infoGrid}>
          <Info label="Dokumenttyp" value={analyse.dokumenttyp} />
          <Info label="Vertragstyp" value={analyse.vertragstyp} />
          <Info label="Provider" value={analyse.provider} />
          <Info label="Vertrauen" value={analyse.vertrauensscore != null ? `${Math.round(analyse.vertrauensscore * 100)}%` : '–'} />
          <Info label="Analysiert" value={formatDate(analyse.analysiert_am)} />
          <Info label="Status" value={analyse.status} />
        </div>

        {kurzfassung.length > 0 && (
          <div>
            <h3 style={subTitle}>Kurzfassung</h3>
            <ul style={{ color: '#cbd5e1', fontSize: 13, paddingLeft: 18, margin: 0, display: 'grid', gap: 4 }}>
              {kurzfassung.map((p, i) => <li key={i}>{p}</li>)}
            </ul>
          </div>
        )}

        {handlungsempfehlung.length > 0 && (
          <div>
            <h3 style={subTitle}>Handlungsempfehlungen</h3>
            {handlungsempfehlung.map((e, i) => (
              <div key={i} style={{ background: '#172554', border: '1px solid #1e40af', borderRadius: 6, padding: '8px 12px', fontSize: 13, color: '#bfdbfe', marginBottom: 7 }}>
                {e}
              </div>
            ))}
          </div>
        )}

        {risiken.length > 0 && (
          <div>
            <h3 style={subTitle}>Risiken ({risiken.length})</h3>
            {risiken.map((r, i) => (
              <RisikoCard key={i} risiko={r} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function RisikoCard({ risiko }) {
  const farbe = RISIKO_FARBE[risiko.stufe] || '#94a3b8';
  return (
    <div style={{ border: `1px solid ${farbe}44`, borderRadius: 7, padding: 10, marginBottom: 8, background: `${farbe}0a` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <span style={{ color: '#f8fafc', fontWeight: 700, fontSize: 13 }}>{risiko.titel}</span>
        <span style={{ ...badge, color: farbe, borderColor: `${farbe}66`, background: `${farbe}14` }}>{risiko.stufe}</span>
      </div>
      <p style={{ color: '#94a3b8', fontSize: 12, margin: '6px 0 4px' }}>{risiko.beschreibung}</p>
      {risiko.empfehlung && <p style={{ color: '#38bdf8', fontSize: 12, margin: 0 }}>💡 {risiko.empfehlung}</p>}
    </div>
  );
}

function ZuordnungTab({ dok, vorschlaege, vertraege, felder, zielVertragId, setZielVertragId, neuerVertragTitel, setNeuerVertragTitel, onZuordnen, onFelderUebernehmen, onNeuerVertrag }) {
  const bestaetigte = felder.filter(f => f.bestaetigt && !f.abgelehnt).length;

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {/* Automatische Vorschläge */}
      {vorschlaege.length > 0 && (
        <section style={panel}>
          <h2 style={sectionTitle}>Automatische Vorschläge</h2>
          {vorschlaege.map(v => (
            <div key={v.vertrag_id} style={{ ...feldCard, borderColor: v.automatisch ? '#34d39944' : '#334155' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                <div>
                  <div style={{ color: '#f8fafc', fontWeight: 700 }}>{v.titel}</div>
                  <div style={{ color: '#94a3b8', fontSize: 12 }}>{v.anbieter} · Score: {Math.round(v.score)} · {v.begruendung}</div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  {konfidenzBadge(v.konfidenz)}
                  <button style={confirmBtn} onClick={() => onZuordnen(v.vertrag_id)}>Zuordnen</button>
                </div>
              </div>
            </div>
          ))}
        </section>
      )}

      {/* Manuelle Zuordnung + Felder übernehmen */}
      <section style={panel}>
        <h2 style={sectionTitle}>Vertrag zuordnen & Felder übernehmen</h2>
        <div style={{ display: 'grid', gap: 10 }}>
          <label style={fieldLabel}>
            <span>Zielvertrag auswählen</span>
            <select value={zielVertragId} onChange={e => setZielVertragId(e.target.value)} style={fieldInput}>
              <option value="">– Vertrag wählen –</option>
              {vertraege.map(v => (
                <option key={v.id} value={v.id}>{v.titel} ({v.anbieter || 'Kein Anbieter'})</option>
              ))}
            </select>
          </label>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button className="btn-primary" onClick={() => zielVertragId && onZuordnen(Number(zielVertragId))} disabled={!zielVertragId}>
              Nur zuordnen
            </button>
            <button className="btn-primary" onClick={onFelderUebernehmen} disabled={!zielVertragId || bestaetigte === 0}>
              Zuordnen + {bestaetigte} Felder übernehmen
            </button>
          </div>
        </div>
      </section>

      {/* Neuen Vertrag erstellen */}
      <section style={panel}>
        <h2 style={sectionTitle}>Neuen Vertrag erstellen</h2>
        <div style={{ display: 'grid', gap: 10 }}>
          <label style={fieldLabel}>
            <span>Vertragsbezeichnung</span>
            <input
              value={neuerVertragTitel}
              onChange={e => setNeuerVertragTitel(e.target.value)}
              placeholder="z.B. Stromvertrag Stadtwerke"
              style={fieldInput}
            />
          </label>
          <button className="btn-primary" onClick={onNeuerVertrag} disabled={!neuerVertragTitel.trim()}>
            Vertrag erstellen und {bestaetigte} Felder übernehmen
          </button>
        </div>
      </section>
    </div>
  );
}

function Preview({ filePath, mimeType, name }) {
  const type = String(mimeType || '').toLowerCase();
  if (type.includes('pdf')) {
    return <iframe title={name} src={filePath} style={previewFrame} />;
  }
  if (type.startsWith('image/')) {
    return <img src={filePath} alt={name} style={previewImage} />;
  }
  return <div style={placeholder}>Keine Vorschau verfügbar.</div>;
}

function Info({ label, value }) {
  return (
    <div style={infoBox}>
      <span style={infoLabel}>{label}</span>
      <span style={infoVal}>{value || '–'}</span>
    </div>
  );
}

function EmptyState({ text }) {
  return <div style={emptyState}>{text}</div>;
}

function Screen({ title = 'Bitte warten', text }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', textAlign: 'center' }}>
      <h2 style={{ color: '#f8fafc', fontSize: 22 }}>{title}</h2>
      <p style={{ color: '#94a3b8' }}>{text}</p>
    </div>
  );
}

function formatDate(v) {
  if (!v) return '–';
  return new Date(String(v).replace(' ', 'T')).toLocaleDateString('de-DE');
}

// ─── Styles ────────────────────────────────────────────────────────────────────
const page = { padding: 18, maxWidth: 1280, margin: '0 auto', display: 'grid', gap: 16 };
const header = { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14, background: '#111827', border: '1px solid #1f2937', borderRadius: 8, padding: 20, flexWrap: 'wrap' };
const eyebrow = { color: '#38bdf8', fontSize: 12, fontWeight: 800, textTransform: 'uppercase' };
const title = { color: '#f8fafc', fontSize: 28, margin: '4px 0 8px', lineHeight: 1.2, overflowWrap: 'anywhere' };
const subtitle = { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' };
const typChip = { color: '#38bdf8', border: '1px solid #155e75', background: '#083344', borderRadius: 999, padding: '4px 10px', fontSize: 11, fontWeight: 800 };
const notice = { color: '#bfdbfe', background: '#172554', border: '1px solid #1d4ed8', borderRadius: 7, padding: '10px 14px', fontSize: 13 };
const twoCol = { display: 'grid', gridTemplateColumns: '320px 1fr', gap: 16, alignItems: 'start' };
const panel = { background: '#111827', border: '1px solid #1f2937', borderRadius: 8, padding: 14 };
const sectionTitle = { color: '#f8fafc', fontSize: 16, fontWeight: 700, margin: '0 0 12px' };
const subTitle = { color: '#cbd5e1', fontSize: 13, fontWeight: 700, margin: '12px 0 8px' };
const tabRow = { display: 'flex', gap: 8, background: '#0f172a', borderRadius: 8, padding: 4 };
const tabBtn = { background: 'transparent', border: 0, color: '#94a3b8', padding: '8px 14px', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: 13 };
const activeTabBtn = { ...tabBtn, background: '#1f2937', color: '#f8fafc' };
const fieldLabel = { display: 'grid', gap: 5, color: '#94a3b8', fontSize: 12, fontWeight: 700 };
const fieldInput = { background: '#0f172a', border: '1px solid #334155', color: '#f8fafc', borderRadius: 7, padding: '10px 9px' };
const confirmBtn = { background: '#064e3b', color: '#34d399', border: '1px solid #065f46', borderRadius: 6, padding: '5px 9px', cursor: 'pointer', fontWeight: 800, fontSize: 13 };
const rejectBtn = { background: '#2b0a0a', color: '#f87171', border: '1px solid #7f1d1d', borderRadius: 6, padding: '5px 9px', cursor: 'pointer', fontWeight: 800, fontSize: 13 };
const badge = { border: '1px solid', borderRadius: 999, padding: '3px 7px', fontSize: 11, fontWeight: 800 };
const feldCard = { background: '#0f172a', border: '1px solid', borderRadius: 7, padding: 10, marginBottom: 8 };
const infoBox = { display: 'grid', gap: 2 };
const infoLabel = { color: '#64748b', fontSize: 11, fontWeight: 800, textTransform: 'uppercase' };
const infoVal = { color: '#e5e7eb', fontSize: 13 };
const infoGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 };
const emptyState = { color: '#94a3b8', background: '#0f172a', border: '1px dashed #334155', borderRadius: 8, padding: 14, fontSize: 13 };
const previewFrame = { width: '100%', height: 420, border: '1px solid #334155', borderRadius: 7, background: '#020617' };
const previewImage = { width: '100%', maxHeight: 420, objectFit: 'contain', border: '1px solid #334155', borderRadius: 7 };
const placeholder = { minHeight: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', background: '#020617', border: '1px solid #334155', borderRadius: 7, padding: 16, textAlign: 'center' };
const statusBox = { display: 'flex', alignItems: 'center', gap: 12, background: '#111827', border: '1px solid #1f2937', borderRadius: 8, padding: '12px 14px', flexWrap: 'wrap' };
const spinner = { fontSize: 18 };
const progressBar = { flex: 1, minWidth: 80, height: 6, background: '#1f2937', borderRadius: 999, overflow: 'hidden', display: 'flex' };
const progressFill = { background: '#2563eb', height: '100%', borderRadius: 999, transition: 'width 0.5s' };
